import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import { Button } from '@cropler/ui-kit';
import dayjs from 'dayjs';

import styles from './styles.module.scss';
import stylesModal from '../../styles/modal.module.scss';

import {
  useGetSensorsDatesQuery,
  useEditLocationMutation,
  sensorsApi,
  useGetSensorsDailySummariesQuery,
} from '../../store/api/sensors';
import { resetSecondarySensor, selectDate, updateSorting } from '../../store/slices/sensors';
import { DemoWorkspaceDate, DemoWorkspaceId } from '../../constants/keys';
import SearchBar from '../SearchBar';
import { useViewport } from '../../hooks';
import { BreakpointMobile, BreakpointMobileUp, ModalWidthSmall } from '../../constants/ui';
import { PhotosTypeSwitch } from '../PhotosTypeSwitch/index.jsx';
import { isDateInPeriod } from '../../utils/dates';
import { CropsSelect } from '../CropsSelect/CropsSelect.jsx';
import { Filter, FilterDate, FilterOrderButton } from '../Filters/index.jsx';
import Panel from '../Panel/index.jsx';
import { goToTimeline } from '../../utils/common';
import { Card } from '../Card/index.jsx';
import { EmptyViewData } from '../EmptyView/index.jsx';
import { ImageZoomModals } from '../ImageZoomModals/index.jsx';
import { DateFormat } from '../../constants/data';
import { Icon as CroplerIcon } from '@cropler/ui-kit';

const SortingData = {
  name: 'location__sensor__name',
  ndvi: 'ndvi__avg',
  temperature: 'air_temperature__avg',
  gdd: 'gdd__value',
  humidity: 'air_moisture__avg',
  dew: 'dew_point__avg',
};

const Sensors = ({ t, currentCrops, seasons, sensorsData }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { widthViewport } = useViewport();

  const {
    sorting: { dir, parameter, crop },
    selectedDate,
    photosType,
  } = useSelector((state) => state.sensors);
  const { currentWorkspaceId } = useSelector((state) => state.workspaces);

  const {
    data: dates = [],
    isFetching: isDatesLoading,
    isSuccess: isDatesSuccess,
  } = useGetSensorsDatesQuery({
    crop_id: crop === 'all' ? undefined : crop,
  });

  const lastDate = dates && dates.length ? dates[dates.length - 1] : null;

  const {
    data: sensors = [],
    isFetching: isSensorsLoading,
    isSuccess: isSensorsSuccess,
  } = useGetSensorsDailySummariesQuery(
    {
      date: selectedDate,
      crop_id: crop === 'all' ? undefined : crop,
      ordering: `${dir}${SortingData[parameter]}`,
      query: params.get('search'),
    },
    {
      skip: !selectedDate,
      refetchOnMountOrArgChange: true,
    },
  );

  const [editPeriod, { isSuccess: isEditPeriodSuccess }] = useEditLocationMutation();

  const [modalSyncOpen, setModalSyncOpen] = useState(false);
  const [selectedSensorData, setSelectedSensorData] = useState(null);
  const [sortingCollapsed, setSortingCollapsed] = useState(null);
  const [cards, setCards] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [fullImage, setFullImage] = useState(false);

  const onSyncLocationDevice = () => {
    if (
      !selectedSensorData ||
      !selectedSensorData.location ||
      !selectedSensorData.location.gps_point ||
      !selectedSensorData.sensorId ||
      !selectedSensorData.install_date ||
      !selectedSensorData.removal_date
    )
      return;
    editPeriod({
      locationId: selectedSensorData.location.id,
      sensorId: selectedSensorData.sensorId,
      name: selectedSensorData.location.name,
      installPoint: {
        type: 'Point',
        coordinates: selectedSensorData.location.gps_point.coordinates,
      },
      installDate: selectedSensorData.install_date,
      removalDate: selectedSensorData.removal_date,
    });
  };

  const openSyncModal = ({ data }) => {
    setSelectedSensorData(data);
    setModalSyncOpen(true);
  };

  const changeDate = (date) => {
    dispatch(selectDate(date));
    setParams({ date: date });
  };

  const onClickCard = ({ id, date }) => {
    goToTimeline({ id, date, navigate });
    dispatch(resetSecondarySensor());
  };

  const onChangeFilterCrop = (value) => {
    if (value.id === 'all') {
      params.delete('tab');
    } else {
      params.set('tab', value.id);
    }
    setParams(params);
    dispatch(updateSorting({ dir, parameter, crop: value.id }));
  };

  const onOpenFullImageModal = ({ status, image }) => {
    setModalOpen(status);
    setFullImage(image);
    if (status && widthViewport < BreakpointMobileUp) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  };

  useEffect(() => {
    if (!isDatesSuccess || isDatesLoading) return;
    // if (!isDatesSuccess || isDatesLoading || !lastDate) return;

    if (!selectedDate) {
      let date = params.get('date');
      if (!date || date === 'null' || !dates.find((d) => d === date)) {
        date = currentWorkspaceId === DemoWorkspaceId ? DemoWorkspaceDate : lastDate ? lastDate : dayjs().format(DateFormat);
      }
      dispatch(selectDate(date));
      params.set('date', date);
      setParams(params);
    }

    if (selectedDate && !dates.find((d) => d === selectedDate)) {
      let date = params.get('date');
      if (!date || !dates.find((d) => d === selectedDate)) {
        date = currentWorkspaceId === DemoWorkspaceId ? DemoWorkspaceDate : lastDate;
      }
      dispatch(selectDate(date));
      params.set('date', date);
      setParams(params);
    }
  }, [lastDate, currentWorkspaceId, isDatesSuccess, isDatesLoading, dates]);

  useEffect(() => {
    if (!isSensorsSuccess || isSensorsLoading || !seasons || !selectedDate || !sensors) return;

    const cardsList = [];

    sensors.map((sensor) => {
      const cardCrop = seasons.find((season) => season.id === sensor.location.fieldSeasonId)?.crop_id;
      const sensorsDataFilter = sensorsData.filter((item) => item.id === sensor.location.id);
      const extendedData = sensorsDataFilter && sensorsDataFilter.length > 0 ? sensorsDataFilter[0] : null;

      const card = {
        ...sensor,
        crop: cardCrop,
        location: {
          ...sensor.location,
          fieldName: extendedData ? extendedData.fieldName : null,
          sensor: extendedData && extendedData.sensor ? extendedData.sensor : null,
        },
        sensorId: extendedData ? extendedData.sensor.id : '',
        points_delta: extendedData ? extendedData.points_delta : '',
        install_date: extendedData ? extendedData.install_date : '',
        removal_date: extendedData ? extendedData.removal_date : '',
      };
      cardsList.push(card);
    });

    const extendedCardList = [...cardsList];
    if (cardsList.length !== sensorsData.length && !params.get('search')) {
      sensorsData.map((item) => {
        if (
          isDateInPeriod(item.install_date, item.removal_date, selectedDate) &&
          !cardsList.find((card) => card.location && card.location.id === item.id)
        ) {
          const cardCrop = seasons.find((season) => season.id === item.field_season_id)?.crop_id;
          if (!cardCrop || cardCrop === crop || crop === 'all') {
            extendedCardList.push({
              ...item,
              crop: cardCrop,
              isEmptyDailySummary: true,
            });
          }
        }
      });
    }

    setCards(extendedCardList);
  }, [selectedDate, sensors, seasons, isSensorsLoading, isSensorsSuccess]);

  useEffect(() => {
    if (isEditPeriodSuccess) {
      dispatch(sensorsApi.util.resetApiState());
    }
  }, [isEditPeriodSuccess]);

  return (
    <div className={`${styles.sensors} container`}>
      {/*Header*/}
      <div className={styles.sensorsHeader}>
        {selectedDate && <FilterDate allDates={dates} selectedDate={selectedDate} selectDate={changeDate} />}
        {widthViewport > BreakpointMobile && (
          <div className={styles.sensorsHeaderCrops}>
            <CropsSelect
              t={t}
              cropsList={currentCrops}
              currentCrop={crop}
              isFilter={true}
              changeCrop={onChangeFilterCrop}
            />
          </div>
        )}
        {widthViewport > BreakpointMobile && (
          <div className={styles.tabSearch}>
            <SearchBar t={t} />
          </div>
        )}
        <PhotosTypeSwitch />
        <FilterOrderButton
          isCustomOrder={true}
          showButtonText={true}
          onChangeOrder={() => setSortingCollapsed(true)}
          t={t}
        />
      </div>

      {/*List*/}
      {!isDatesLoading && isDatesSuccess && dates && dates.length === 0 && (!cards || cards.length === 0) && (
        <div className={styles.sensorsEmpty}>
          <EmptyViewData t={t} isDashboard={true} />
        </div>
      )}

      {cards && cards.length > 0 && (
        <div className={styles.cardList}>
          {cards.map((item) =>
            item.isEmptyDailySummary ? (
              <Card
                key={item.id}
                t={t}
                name={item.sensor.name}
                crop={item.crop}
                pointsDelta={item.points_delta}
                location={{
                  id: item.id,
                  gps_point: item.gps_point,
                  install_point: item.install_point,
                  fieldName: item.fieldName,
                  name: item.name,
                  sensor: item.sensor,
                }}
                isAiEnable={
                  item.sensor.settings.enabled &&
                  item.sensor.settings.enabled.find((item) => item.name === 'ai_photo_analysis')
                }
                isNoData={true}
                onClickSync={() => openSyncModal({ data: item })}
              />
            ) : (
              <Card
                key={item.id}
                t={t}
                name={item.name}
                image={photosType === 'day' || photosType === 'ndvi' ? item.imageUrlDay : item.imageUrlNight}
                crop={item.crop}
                location={item.location}
                bbchAI={item.bbchAI}
                temperature={item.temperature}
                gdd={Math.floor(item.ggd)}
                dewDuration={item.dewDuration}
                ndvi={item.ndvi}
                soilMoisture={item.soilMoisture}
                soilTemperature={item.soilTemperature}
                isAiEnable={item.isAiEnable}
                pointsDelta={item.points_delta}
                installDate={item.install_date}
                removalDate={item.removal_date}
                onClick={() => onClickCard({ id: item.location.id, date: selectedDate })}
                onClickSync={() => openSyncModal({ data: item })}
                onClickExpandImage={() =>
                  onOpenFullImageModal({
                    status: true,
                    image: photosType === 'day' || photosType === 'ndvi' ? item.imageUrlDay : item.imageUrlNight,
                  })
                }
              />
            ),
          )}
        </div>
      )}

      {/*Confirm Location Sync*/}
      <Modal
        title={t('card.syncTitle')}
        open={modalSyncOpen}
        onCancel={() => setModalSyncOpen(false)}
        footer={false}
        centered
        width={ModalWidthSmall}
      >
        <p dangerouslySetInnerHTML={{ __html: t('card.syncText') }}></p>
        <div className={stylesModal.modalNav}>
          <Button type="secondary" onClick={() => setModalSyncOpen(false)}>
            {t('actions.cancel')}
          </Button>
          <Button type="primary" icon={<CroplerIcon name="check" />} onClick={() => onSyncLocationDevice()}>
            {t('card.syncButton')}
          </Button>
        </div>
      </Modal>

      {/*Filter*/}
      {sortingCollapsed && (
        <Panel title={t('filters.title')} setActivePanel={() => setSortingCollapsed(false)}>
          <Filter t={t} workspaceCrops={currentCrops} />
        </Panel>
      )}

      {/*Full image modal*/}
      <ImageZoomModals
        fullImage={fullImage}
        modalOpen={modalOpen}
        onCloseModal={() => onOpenFullImageModal({ status: false, image: '' })}
      />
    </div>
  );
};

export default Sensors;
